import React from 'react';

import { RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { DemandUsageOverrideDTO } from '@recurrency/core-api-schema/dist/ml/getDemandUsageOverrides';
import { MonthlyDemandUsageRecordDTO } from '@recurrency/core-api-schema/dist/ml/getMonthlyDemandUsage';
import { ColumnType } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import { Moment } from 'moment';
import moment from 'moment/moment';

import { FlexSpace } from 'components/FlexSpace';
import { Table } from 'components/Table';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useCoreApi } from 'hooks/useApi';

import { truthy } from 'utils/boolean';
import { formatNumber } from 'utils/formatting';
import { sortableNumberColumn } from 'utils/tables';

import { Button } from '../Button';
import { UsageOverrideTooltipText } from './UsageOverrideTooltipText';

export const MonthlyUsageTable = ({
  parentIsLoading = false,
  itemId,
  locationId,
  onLinesActionClick,
  usageOverrides,
}: {
  parentIsLoading: boolean;
  itemId: string;
  locationId: string;
  onLinesActionClick: (fromDate: Moment, toDate: Moment) => void;
  usageOverrides?: DemandUsageOverrideDTO[];
}) => {
  const { data: monthlyUsageData, isLoading } = useCoreApi(schemas.ml.getMonthlyDemandUsage, {
    queryParams: {
      itemId,
      locationId,
    },
  });

  const hasHistoricalDemand = (monthlyUsageData?.items || []).some((item) => item.inheritedUsageQty !== 0);

  const tableColumns: (ColumnType<MonthlyDemandUsageRecordDTO> | null)[] = [
    {
      title: 'Year',
      dataIndex: 'usageDate',
      defaultSortOrder: 'descend' as SortOrder,
      render: (value: string) => moment.utc(value).format('YYYY'),
    },
    {
      title: (
        <div
          className={css`
            padding-left: 50%;
          `}
        >
          Month
        </div>
      ),
      dataIndex: 'usageDate',
      render: (value: string) => (
        <div
          className={css`
            padding-left: 50%;
          `}
        >
          {moment.utc(value).format('MMMM')}
        </div>
      ),
    },
    {
      ...sortableNumberColumn({
        title: 'Total Usage',
        dataIndex: 'baseUsageQty',
      }),
      render: (value: number, record: MonthlyDemandUsageRecordDTO) => {
        const override = usageOverrides?.find(
          (uo) =>
            uo.usageDate === moment.utc(record.usageDate).format('YYYY-MM-DD') &&
            uo.locationId === locationId &&
            uo.itemId === itemId,
        );

        if (override) {
          return (
            <FlexSpace justify="flex-end" gap={0}>
              {formatNumber(value)}
              <InfoTooltip
                title={
                  <UsageOverrideTooltipText
                    value={value}
                    unitOfMeasure={record.unitOfMeasure}
                    updatedBy={override.updatedByUserName}
                    updatedAt={override.updatedAt}
                    note={override.overrideNote}
                  />
                }
                useInfoIcon
              />
            </FlexSpace>
          );
        }

        return formatNumber(value);
      },
    },
    hasHistoricalDemand
      ? sortableNumberColumn({
          title: 'Inherited Usage',
          dataIndex: 'inheritedUsageQty',
        })
      : null,
    {
      title: 'UOM',
      dataIndex: 'unitOfMeasure',
    },
    {
      title: 'Actions',
      dataIndex: 'usageDate',
      width: '100px',
      render: (value: string) => (
        <Button
          size="small"
          onClick={() => {
            onLinesActionClick(moment.utc(value).startOf('month'), moment.utc(value).endOf('month'));
          }}
        >
          Lines <RightOutlined />
        </Button>
      ),
    },
  ].filter(truthy);

  return (
    <Table
      pageSize={10}
      isLoading={parentIsLoading || isLoading}
      data={monthlyUsageData?.items.sort((a, b) => (moment(a.usageDate).isBefore(b.usageDate) ? 1 : -1)) || []}
      columns={tableColumns.filter(truthy)}
    />
  );
};
